/**
 * Audio UI element for blocks that are audio assets (blob/* + media/*
 * applogs per docs/schema/asset.md, e.g. voice notes from BulletMenu →
 * Record audio): one compact row with the inline player, the pipeline
 * actions dot menu and the transcript toggle; the expanded transcript
 * unfolds below. Renders nothing for non-audio blocks. The block's text
 * content stays the user's editable title — this element is the audio's
 * own representation.
 *
 * Source resolution is local-first: OPFS bytes (recorded on this device)
 * → object URL; otherwise `/ipfs/<cid>` (service worker fetches gateways).
 *
 * Attribute access uses useLiveEntityAt (subscription-based) — a plain
 * queryAndMap in a memo is NOT reactive to thread inserts, so the player
 * would only appear after a remount (the blob/* applogs land at record-stop,
 * long after this component mounted with the record-start block).
 */

import type { EntityID } from '@wovin/core'
import type { Component } from 'solid-js'
import { useLiveEntityAt } from '@wovin/hooks-solid'
import { createMemo, createResource, createSignal, onCleanup, Show } from 'solid-js'
import { readBlobFromOpfs } from '../audio/blob-upload'
import { useCurrentThread } from '../ui/reactive'
import { AudioActionsMenu } from './AudioActionsMenu'
import { TranscriptSegments, TranscriptToggle, useAnnotationSegments } from './TranscriptView'

export const AudioPlayer: Component<{ blockID?: EntityID }> = (props) => {
	const thread = useCurrentThread()
	const { blockID } = props // ! non-reactive (stable per BlockContent instance)
	if (!blockID) return null

	const mimeType = useLiveEntityAt<string>(blockID, 'blob/mimeType', { thread })
	const cid = useLiveEntityAt<string>(blockID, 'blob/cid', { thread })

	const audioCid = createMemo(() => {
		if (!String(mimeType() ?? '').startsWith('audio/')) return null
		return (cid() as string | null) ?? null
	})

	const [src] = createResource(audioCid, async (cidStr) => {
		const local = await readBlobFromOpfs(cidStr)
		if (local) {
			const url = URL.createObjectURL(local)
			onCleanup(() => URL.revokeObjectURL(url))
			return url
		}
		return `/ipfs/${cidStr}`
	})

	const segments = useAnnotationSegments(blockID, thread)
	const [transcriptOpen, setTranscriptOpen] = createSignal(false)

	return (
		<Show when={src()}>
			<div class='max-w-full flex flex-col self-start'>
				<div class='flex items-center gap-1.5'>
					{/* mb-0 overrides a global typography margin on <audio> that broke row centering */}
					<audio controls preload='metadata' src={src()!} class='mb-0 h-8 max-w-full' />
					<AudioActionsMenu blockID={blockID} />
					<TranscriptToggle
						blockID={blockID}
						segments={segments}
						open={transcriptOpen}
						onToggle={() => setTranscriptOpen(v => !v)}
					/>
				</div>
				<Show when={transcriptOpen() && segments().length}>
					<TranscriptSegments segments={segments} />
				</Show>
			</div>
		</Show>
	)
}
